import { useState } from 'react';
import { FloatingText, PassiveAbilityNotification } from '../components/BattleEffects';
import { BattleFxEntry } from '../components/BattleFx';
import { PassiveEvent, BattleUnitState } from '../types/battle';

export function useBattleEffects() {
  const [floatingTexts, setFloatingTexts] = useState<FloatingText[]>([]);
  const [passiveNotifications, setPassiveNotifications] = useState<PassiveAbilityNotification[]>([]);
  const [fxEntries, setFxEntries] = useState<BattleFxEntry[]>([]);
  const [turnTransitionBanner, setTurnTransitionBanner] = useState<{ visible: boolean; unitName: string; isPlayer: boolean } | null>(null);

  const addFloatingText = (text: string, type: FloatingText['type'], x: number, y: number) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    setFloatingTexts(prev => [...prev, { id, text, type, x, y }]);
  };

  const addPassiveNotification = (
    abilityName: string,
    unitName: string,
    description: string,
    type: PassiveAbilityNotification['type']
  ) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    setPassiveNotifications(prev => [...prev, { id, abilityName, unitName, description, type }]);
  };

  const removeFloatingText = (id: string) => {
    setFloatingTexts(prev => prev.filter(text => text.id !== id));
  };

  const removePassiveNotification = (id: string) => {
    setPassiveNotifications(prev => prev.filter(n => n.id !== id));
  };

  const removeFxEntry = (id: string) => {
    setFxEntries(prev => prev.filter(e => e.id !== id));
  };

  /**
   * Spawn a projectile that travels from attacker tile to target tile,
   * followed by an impact burst at the target.
   */
  const spawnFx = (
    skillOrAction: string,
    fromPos: { x: number; y: number },
    toPos:   { x: number; y: number },
    tileSize: number = 80
  ) => {
    const idBase = Date.now().toString() + Math.random().toString(36).substr(2, 5);
    const fromPx = fromPos.x * tileSize + tileSize / 2;
    const fromPy = fromPos.y * tileSize + tileSize / 2;

    const projectile: BattleFxEntry = {
      id: `${idBase}-proj`,
      type: 'projectile',
      skillOrAction,
      targetX: toPos.x,
      targetY: toPos.y,
      fromPixelX: fromPx,
      fromPixelY: fromPy,
      duration: 450,
    };
    setFxEntries(prev => [...prev, projectile]);

    // Impact fires after the projectile arrives
    setTimeout(() => {
      const impact: BattleFxEntry = {
        id: `${idBase}-impact`,
        type: 'impact',
        skillOrAction,
        targetX: toPos.x,
        targetY: toPos.y,
        duration: 300,
      };
      setFxEntries(prev => [...prev, impact]);
    }, 420);
  };

  const showTurnTransition = (unitName: string, isPlayer: boolean) => {
    setTurnTransitionBanner({ visible: true, unitName, isPlayer });
    setTimeout(() => setTurnTransitionBanner(null), 2000);
  };

  const processPassiveEvents = (
    passiveEvents: PassiveEvent[],
    battleUnits: BattleUnitState[],
    addEnhancedBattleLog: (type: string, data: Record<string, unknown>) => void
  ) => {
    if (!passiveEvents || passiveEvents.length === 0) return;

    passiveEvents.forEach((event) => {
      addPassiveNotification(
        event.abilityName,
        event.unitName,
        event.description,
        event.type as PassiveAbilityNotification['type']
      );

      addEnhancedBattleLog('passive_ability', {
        unitName: event.unitName,
        abilityName: event.abilityName,
        description: event.description,
      });

      if (event.eventData) {
        let floatingText = '';
        let textType: FloatingText['type'] = 'passive';

        if (event.eventData.healing) {
          floatingText = `+${event.eventData.healing} HP`;
          textType = 'healing';
        } else if (event.eventData.damage) {
          floatingText = `${event.eventData.damage} DMG`;
          textType = 'damage';
        } else if (event.eventData.apGained) {
          floatingText = `+${event.eventData.apGained} AP`;
          textType = 'passive';
        }

        if (floatingText) {
          const unit = battleUnits.find(
            (u) => u.character?.name === event.unitName || u.enemyName === event.unitName
          );
          if (unit) {
            const x = unit.position.x * 80 + 40;
            const y = unit.position.y * 80 + 40;
            addFloatingText(floatingText, textType, x, y);
          }
        }
      }
    });
  };

  return {
    floatingTexts,
    passiveNotifications,
    fxEntries,
    turnTransitionBanner,
    addFloatingText,
    addPassiveNotification,
    removeFloatingText,
    removePassiveNotification,
    removeFxEntry,
    spawnFx,
    showTurnTransition,
    processPassiveEvents,
  };
}
